import type { ActionReducer, MetaReducer } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';

import type { AppState } from './app-reducers';

// Factory function that creates a storage object compatible with localStorageSync
function createEncryptedStorage(encryptionService: {
  encrypt(value: string): string;
  decrypt(value: string | null): string | null;
}): Storage {
  return {
    // plain number property, required by localStorageSync
    length: localStorage.length,

    clear(): void {
      localStorage.clear();
    },

    key(index: number): string | null {
      return localStorage.key(index);
    },

    setItem(key: string, value: string): void {
      const ENCRYPTED_VAL = encryptionService.encrypt(value);
      localStorage.setItem(key, ENCRYPTED_VAL);
    },

    getItem(key: string): string | null {
      const ENCRYPTED_VAL = localStorage.getItem(key);
      return encryptionService.decrypt(ENCRYPTED_VAL);
    },

    removeItem(key: string): void {
      localStorage.removeItem(key);
    },
  };
}

// Main meta-reducer factory
export function createLocalStorageSyncReducer(encryptionService: {
  encrypt(value: string): string;
  decrypt(value: string | null): string | null;
}): MetaReducer<AppState> {
  const STORAGE = createEncryptedStorage(encryptionService);

  return (reducer: ActionReducer<AppState>): ActionReducer<AppState> => {
    return localStorageSync({
      keys: [
        'permission',
        {
          views: ['profileImage', 'profile', 'pricingSettings', 'selectedListTabs'],
        },
      ],
      rehydrate: true,
      storage: STORAGE, // <-- our encrypted storage
    })(reducer);
  };
}

// Optional: default export if needed
export function localStorageSyncReducer(reducer: ActionReducer<AppState>): ActionReducer<AppState> {
  return reducer;
}
